import React, { useEffect, useMemo, useRef } from 'react';
import { Group, Circle, Text, Rect } from 'react-konva';
import Konva from 'konva';
import { useTranslation } from '../../../hooks/useTranslation';

interface OrgCollapsedDotGridProps {
  x: number;
  y: number;
  parentWidth: number;
  parentHeight?: number;
  count: number;
  /** Per-dot colors from heatmap/display rules. Falls back to gray for missing entries. */
  dotColors?: string[];
  /** Per-dot selection highlight (secondary report to a selected node): larger radius + white ring. */
  dotHighlighted?: boolean[];
  onClick?: () => void;
}

const DOT_SIZE = 8;
const HIGHLIGHTED_DOT_RADIUS = DOT_SIZE / 2 + 1;
const HIGHLIGHTED_DOT_STROKE = '#FFFFFF';
const HIGHLIGHTED_DOT_STROKE_WIDTH = 1.5;
const DOT_GAP = 3;
const DOTS_PER_ROW = 5;
const PADDING_X = 8;
const PADDING_Y = 6;
const LABEL_HEIGHT = 13;
const DEFAULT_DOT_COLOR = '#94A3B8';

export const OrgCollapsedDotGrid: React.FC<OrgCollapsedDotGridProps> = React.memo(({
  x,
  y,
  parentWidth,
  parentHeight = 0,
  count,
  dotColors,
  dotHighlighted,
  onClick,
}) => {
  const { t } = useTranslation('diagram');
  const groupRef = useRef<Konva.Group>(null);
  const didAnimateRef = useRef(false);

  const peopleLabel = useMemo(
    () =>
      t('orgchart.collapsedGroup.peopleCount', {
        count,
        defaultValue: `${count} people`,
      }) as string,
    [count, t],
  );

  const displayCount = Math.min(Math.max(count, 0), 50);
  const cols = Math.min(displayCount, DOTS_PER_ROW);
  const rows = Math.ceil(displayCount / DOTS_PER_ROW) || 0;
  const gridWidth = cols > 0 ? cols * (DOT_SIZE + DOT_GAP) - DOT_GAP : 0;
  const gridHeight = rows > 0 ? rows * (DOT_SIZE + DOT_GAP) - DOT_GAP : 0;
  const totalWidth = Math.max(gridWidth + PADDING_X * 2, 60);
  const totalHeight = gridHeight + PADDING_Y * 2 + LABEL_HEIGHT;

  // Opacity-only fade-in — does not touch x/y/offset so it cannot fight
  // with layout-driven position updates that arrive after initial render.
  useEffect(() => {
    if (didAnimateRef.current || count <= 0) return;
    const el = groupRef.current;
    if (!el) return;
    didAnimateRef.current = true;

    el.opacity(0);

    const tween = new Konva.Tween({
      node: el,
      duration: 0.25,
      opacity: 1,
      easing: Konva.Easings.EaseOut,
    });
    tween.play();
    return () => { tween.destroy(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (count <= 0) return null;

  const offsetX = parentWidth > 0 ? (parentWidth - totalWidth) / 2 : 0;
  const offsetY = parentHeight > 0 ? (parentHeight - totalHeight) / 2 : 0;

  return (
    <Group
      ref={groupRef}
      x={x + offsetX}
      y={y + offsetY}
      onClick={onClick}
      onTap={onClick}
      onMouseEnter={(e) => { if (onClick) { const c = e.target.getStage()?.container(); if (c) c.style.cursor = 'pointer'; } }}
      onMouseLeave={(e) => { if (onClick) { const c = e.target.getStage()?.container(); if (c) c.style.cursor = 'default'; } }}
    >
      <Rect
        width={totalWidth}
        height={totalHeight}
        cornerRadius={6}
        fill="#F8FAFC"
        stroke="#E2E8F0"
        strokeWidth={1}
      />
      <Text
        x={0}
        y={PADDING_Y - 2}
        width={totalWidth}
        text={peopleLabel}
        fontSize={9}
        fill="#94A3B8"
        fontFamily="Inter, system-ui, sans-serif"
        align="center"
      />
      <Group x={(totalWidth - gridWidth) / 2} y={PADDING_Y + LABEL_HEIGHT}>
        {Array.from({ length: displayCount }, (_, i) => {
          const col = i % DOTS_PER_ROW;
          const row = Math.floor(i / DOTS_PER_ROW);
          const isHighlighted = dotHighlighted?.[i] === true;
          const fill = dotColors?.[i] || DEFAULT_DOT_COLOR;
          const radius = isHighlighted ? HIGHLIGHTED_DOT_RADIUS : DOT_SIZE / 2;
          return (
            <Circle
              key={i}
              x={col * (DOT_SIZE + DOT_GAP) + DOT_SIZE / 2}
              y={row * (DOT_SIZE + DOT_GAP) + DOT_SIZE / 2}
              radius={radius}
              fill={fill}
              stroke={isHighlighted ? HIGHLIGHTED_DOT_STROKE : undefined}
              strokeWidth={isHighlighted ? HIGHLIGHTED_DOT_STROKE_WIDTH : 0}
            />
          );
        })}
      </Group>
    </Group>
  );
});

OrgCollapsedDotGrid.displayName = 'OrgCollapsedDotGrid';
